import type { ReactNode } from "react";
import type { SortBy } from "../lib/search";
import GridIcon from "../icons/GridIcon";
import ListIcon from "../icons/ListIcon";
import CheckIcon from "../icons/CheckIcon";
import styles from "./CatalogToolbar.module.css";

/** Re-exported so existing `import type { SortOption }` call sites keep working. */
export type SortOption = SortBy;
export type ViewMode = "grid" | "list";

// Ordered so each default heads the list in the mode it applies to: Best Match while
// searching (offered only then), Most Used while browsing.
const SORT_OPTIONS: { value: SortOption; label: string }[] = [
  { value: "relevance", label: "Best Match" },
  { value: "mostUsed", label: "Most Used" },
  // Community rating (#617/#618), directly after Most Used: the two are the epic's signal
  // sorts and the card pairs them in one row, so the dropdown keeps them adjacent.
  // Not the default — adoption covers 42.4% of the catalog while ratings need 5+ votes
  // per entry (#620), so rating coverage starts at zero and grows only as people vote.
  { value: "topRated", label: "Highest Rated" },
  { value: "name", label: "Name" },
  { value: "updated", label: "Recently Updated" },
  { value: "created", label: "Newest First" },
  { value: "official", label: "Official CAI first" },
];

interface CatalogToolbarProps {
  sortBy: SortOption;
  onSortChange: (sort: SortOption) => void;
  /**
   * Whether to offer "Best Match". Only meaningful with an active search query —
   * with an empty box there are no relevance scores to order by, so offering it
   * would be a sort that does nothing, which is the problem #590 set out to fix.
   */
  showRelevance: boolean;
  viewMode: ViewMode;
  onViewChange: (view: ViewMode) => void;
  officialOnly: boolean;
  onOfficialToggle: () => void;
  children?: ReactNode;
}

export default function CatalogToolbar({ sortBy, onSortChange, showRelevance, viewMode, onViewChange, officialOnly, onOfficialToggle, children }: CatalogToolbarProps) {
  const sortOptions = showRelevance
    ? SORT_OPTIONS
    : SORT_OPTIONS.filter((opt) => opt.value !== "relevance");

  return (
    <div className={styles.toolbar}>
      {children}
      <div className={styles.bottomRow}>
        <div className={styles.officialRow}>
          <span className={styles.officialLabel}>Official:</span>
          <button
            type="button"
            className={`${styles.officialToggle} ${officialOnly ? styles.officialToggleActive : ""}`}
            onClick={onOfficialToggle}
            aria-pressed={officialOnly}
            title="Show only Official CAI plug-ins and skills"
          >
            <span className={styles.officialToggleCheck} aria-hidden="true"><CheckIcon /></span>
            Official CAI only
          </button>
        </div>
        <div className={styles.right}>
          <label className={styles.sortLabel} htmlFor="catalog-sort">Sort:</label>
          <select
            id="catalog-sort"
            className={styles.sortSelect}
            value={sortBy}
            onChange={(e) => onSortChange(e.target.value as SortOption)}
          >
            {sortOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>

          <div className={styles.viewToggle} role="group" aria-label="View mode">
            <button
              className={`${styles.viewButton} ${viewMode === "grid" ? styles.active : ""}`}
              onClick={() => onViewChange("grid")}
              aria-label="Grid view"
              aria-pressed={viewMode === "grid"}
              type="button"
            >
              <GridIcon />
            </button>
            <button
              className={`${styles.viewButton} ${viewMode === "list" ? styles.active : ""}`}
              onClick={() => onViewChange("list")}
              aria-label="List view"
              aria-pressed={viewMode === "list"}
              type="button"
            >
              <ListIcon />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
